/**
 * Native shift GPS tracker (Android foreground service, every 15 minutes).
 * No-op on web / non-native platforms.
 */

import { registerPlugin } from "@capacitor/core";
import { isNative } from "./mobile";
import { resolveApiBaseUrl } from "../api/client";

const ShiftLocationTracker = registerPlugin("ShiftLocationTracker");

function apiBase() {
  try {
    return resolveApiBaseUrl();
  } catch {
    return "";
  }
}

export async function syncShiftTrackConfig() {
  if (!isNative()) return;
  try {
    await ShiftLocationTracker.syncConfig({ apiBaseUrl: apiBase() });
  } catch {
    // plugin missing on older APKs
  }
}

export async function startShiftLocationTracking() {
  if (!isNative()) return { skipped: true, reason: "web" };
  await syncShiftTrackConfig();
  try {
    const status = await ShiftLocationTracker.start({
      apiBaseUrl: apiBase(),
    });
    return status;
  } catch (err) {
    return {
      skipped: true,
      reason: err?.message || "start_failed",
    };
  }
}

export async function stopShiftLocationTracking() {
  if (!isNative()) return { skipped: true };
  try {
    return await ShiftLocationTracker.stop();
  } catch {
    return { skipped: true };
  }
}

export async function pauseShiftLocationTracking() {
  if (!isNative()) return { skipped: true };
  try {
    await syncShiftTrackConfig();
    return await ShiftLocationTracker.pause();
  } catch {
    return { skipped: true };
  }
}

export async function resumeShiftLocationTracking() {
  if (!isNative()) return { skipped: true };
  try {
    await syncShiftTrackConfig();
    return await ShiftLocationTracker.resume();
  } catch (err) {
    return { skipped: true, reason: err?.message || "resume_failed" };
  }
}

export async function getShiftLocationTrackingStatus() {
  if (!isNative()) {
    return { state: "stopped", running: false, backgroundGranted: false };
  }
  try {
    return await ShiftLocationTracker.getStatus();
  } catch {
    return { state: "stopped", running: false, backgroundGranted: false };
  }
}

export async function openShiftLocationSettings() {
  if (!isNative()) return;
  try {
    await ShiftLocationTracker.openLocationSettings();
  } catch {
    // ignore
  }
}

/**
 * Align native tracker with the current open APP attendance session.
 * @param {{ checkInTime?: unknown, checkOutTime?: unknown, breakOutTime?: unknown, breakInTime?: unknown } | null} session
 */
export async function syncShiftTrackingWithSession(session) {
  if (!isNative()) return;
  const checkedIn = Boolean(session?.checkInTime);
  const checkedOut = Boolean(session?.checkOutTime);
  const onBreak =
    Boolean(session?.breakOutTime) && !session?.breakInTime;

  if (!checkedIn || checkedOut) {
    await stopShiftLocationTracking();
    return;
  }
  if (onBreak) {
    await pauseShiftLocationTracking();
    return;
  }
  await startShiftLocationTracking();
}
